"use client";

import { StatCard, StatGrid } from "@/components/tool-kit/primitives";
import { HINT, WIDGET_H2, WIDGET_H3 } from "@/components/tool-kit/styles";
import type { MCNEMAR_TEST_CALCULATOR_STRINGS } from "@/components/tools/mcnemar-test-calculator";
import {
  ApaReport,
  Decision,
  ResultTable,
  Working,
} from "@/components/tools/stats-tests/kit";
import { t } from "@/lib/i18n/interpolate";
import {
  apaP,
  fixed,
  formatP,
  formatStat,
  type NumberFormatter,
  richTemplate,
} from "@/lib/tool-kit/stats-tests/format";
import type { McNemarResult } from "@/lib/tool-kit/stats-tests/mcnemar";

/** Results for the McNemar test calculator. */

type Strings = typeof MCNEMAR_TEST_CALCULATOR_STRINGS;

export type McNemarMethod = "exact" | "corrected" | "uncorrected";

/** Below this many discordant pairs the χ² approximation is rough. */
const SMALL_DISCORDANT = 25;

function chosen(r: McNemarResult, method: McNemarMethod) {
  if (method === "corrected") {
    return { chi: r.chiSquareCorrected, p: r.pChiSquareCorrected };
  }
  if (method === "uncorrected") {
    return { chi: r.chiSquare, p: r.pChiSquare };
  }
  return { chi: null, p: r.pExact };
}

/** π from an odds value, for the working line. */
function toProportion(odds: number): number {
  return Number.isFinite(odds) ? odds / (1 + odds) : 1;
}

export function McNemarResults({
  r,
  method,
  alpha,
  strings,
  fmt,
}: {
  r: McNemarResult;
  method: McNemarMethod;
  alpha: number;
  strings: Strings;
  fmt: NumberFormatter;
}) {
  const [a, b, c, d] = r.table;
  const n = (v: number) => formatStat(v, fmt);
  const level = fmt(r.level, { style: "percent", maximumFractionDigits: 1 });
  const { chi, p } = chosen(r, method);
  const values = {
    n: fmt(r.n),
    chi: chi === null ? "" : fixed(chi, 2, fmt),
    p: apaP(p, fmt),
    or: fixed(r.oddsRatio, 2, fmt),
    level,
    low: fixed(r.ciLow, 2, fmt),
    high: fixed(r.ciHigh, 2, fmt),
  };
  const apa = richTemplate(
    chi === null ? strings.apaExact : strings.apaChi,
    values
  );
  const pct = (v: number) =>
    fmt(v, { style: "percent", maximumFractionDigits: 1 });
  return (
    <section aria-live="polite" className="space-y-4">
      <h2 className={WIDGET_H2}>{strings.results}</h2>
      <StatGrid>
        <StatCard
          emphasis
          label={strings.pExact}
          value={formatP(r.pExact, fmt)}
        />
        <StatCard
          label={strings.chiCorrected}
          value={n(r.chiSquareCorrected)}
        />
        <StatCard label={strings.chiUncorrected} value={n(r.chiSquare)} />
        <StatCard label={strings.oddsRatio} value={n(r.oddsRatio)} />
        <StatCard
          label={t(strings.ci, { level })}
          value={`[${n(r.ciLow)}, ${n(r.ciHigh)}]`}
        />
        <StatCard label={strings.discordant} value={fmt(r.discordant)} />
        <StatCard
          label={strings.proportions}
          value={`${pct(r.p1)}, ${pct(r.p2)}`}
        />
      </StatGrid>
      <Decision reject={p < alpha}>
        {t(p < alpha ? strings.decisionReject : strings.decisionKeep, {
          alpha: fmt(alpha),
          p: formatP(p, fmt),
        })}
      </Decision>
      <p className={HINT}>
        {t(strings.discordantNote, {
          a: fmt(a),
          b: fmt(b),
          c: fmt(c),
          d: fmt(d),
        })}
      </p>
      {r.discordant < SMALL_DISCORDANT ? (
        <p className={HINT}>{strings.smallDiscordant}</p>
      ) : null}
      <h3 className={WIDGET_H3}>{strings.methodsHeading}</h3>
      <ResultTable
        head={[strings.methodColumn, strings.statisticColumn, strings.pColumn]}
        rows={[
          {
            key: "exact",
            cells: [strings.methodExact, "—", formatP(r.pExact, fmt)],
          },
          {
            key: "corrected",
            cells: [
              strings.methodCorrected,
              n(r.chiSquareCorrected),
              formatP(r.pChiSquareCorrected, fmt),
            ],
          },
          {
            key: "uncorrected",
            cells: [
              strings.methodUncorrected,
              n(r.chiSquare),
              formatP(r.pChiSquare, fmt),
            ],
          },
        ]}
      />
      <Working heading={strings.howCalculated}>
        <p>
          b = {fmt(b)}, c = {fmt(c)}, b + c = {fmt(r.discordant)}
        </p>
        <p>
          χ² = (b − c)² / (b + c) = ({fmt(b)} − {fmt(c)})² / {fmt(r.discordant)}{" "}
          = {n(r.chiSquare)}; p = {formatP(r.pChiSquare, fmt)}
        </p>
        <p>
          χ²<sub>c</sub> = (|b − c| − 1)² / (b + c) = ({fmt(Math.abs(b - c))} −
          1)² / {fmt(r.discordant)} = {n(r.chiSquareCorrected)}; p ={" "}
          {formatP(r.pChiSquareCorrected, fmt)}
        </p>
        <p>
          {t(strings.workingExact, {
            min: fmt(Math.min(b, c)),
            n: fmt(r.discordant),
            p: formatP(r.pExact, fmt),
          })}
        </p>
        <p>
          OR = b / c = {fmt(b)} / {fmt(c)} = {n(r.oddsRatio)}
        </p>
        <p>
          {t(strings.workingCi, {
            pi: n(b / r.discordant),
            piLow: n(toProportion(r.ciLow)),
            piHigh: n(toProportion(r.ciHigh)),
          })}
        </p>
      </Working>
      <ApaReport
        copiedLabel={strings.copied}
        copyLabel={strings.copyApa}
        heading={strings.apaHeading}
        note={strings.apaNote}
        parts={apa}
      />
    </section>
  );
}
